import React, { useState, useEffect, useMemo } from 'react';
import RichText from '@/components/RichText';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import RichTextEditor from '@/components/RichTextEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Shield, UserCheck, User, Ban, RotateCcw, Trash2, KeyRound, CheckSquare, Square, MinusSquare, UserPlus, Eye, Pencil, Save, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { getTraitTags, TAG_COLORS } from '@/utils/behavioural';
import MemberAvatar from '@/components/MemberAvatar';

const BACKEND_URL = '';

export function AdminUsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [photoBust, setPhotoBust] = useState({});
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users`, { credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch {}
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/role?new_role=${newRole}`, {
        method: 'PUT', credentials: 'include'
      });
      if (!res.ok) { const text = await res.text(); try { throw new Error(JSON.parse(text).detail); } catch(e) { if (e.message) throw e; throw new Error('Role update failed'); } }
      toast.success(`User role updated to ${newRole}`);
      loadUsers();
    } catch (e) { toast.error(e.message || 'Failed to update role'); }
  };

  const handleSuspendUser = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/suspend`, {
        method: 'PUT', credentials: 'include'
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Request failed (${res.status})`); }
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadUsers();
    } catch (e) { toast.error(e.message || 'Failed to update user status'); }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!(await window.appConfirm(`Are you sure you want to permanently delete ${userName}? This cannot be undone.`))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE', credentials: 'include'
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(res.ok ? 'Deleted' : `Request failed (${res.status})`); }
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      toast.success('User deleted');
      setSelectedIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleAdminUploadPhoto = async (userId, userName, file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/members/${userId}/upload-photo`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      toast.success(`Profile photo updated for ${userName}`);
      setPhotoBust((prev) => ({ ...prev, [userId]: Date.now() }));
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleForcePasswordChange = async (userId, userName) => {
    if (!(await window.appConfirm(`Force "${userName}" to change their password? They will receive an email and must change it on next login.`))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/force-password-change`, {
        method: 'POST', credentials: 'include'
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(res.ok ? 'Unexpected response' : `Request failed (${res.status})`); }
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleApproveUser = async (userId, userName) => {
    if (!(await window.appConfirm(`Approve "${userName}"? They will receive an email with a login link.`))) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/approve`, {
        method: 'POST', credentials: 'include'
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(res.ok ? 'Unexpected response' : `Request failed (${res.status})`); }
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = !userSearch ||
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.profile?.organisation?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
    const matchesStatus = userStatusFilter === 'all' ||
      (userStatusFilter === 'suspended' ? u.status === 'suspended' : u.status !== 'suspended');
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Only non-admin, non-self users can be selected
  const selectableUsers = useMemo(() =>
    filteredUsers.filter(u => u.user_id !== currentUser?.user_id && u.role !== 'admin'),
    [filteredUsers, currentUser]
  );

  const allSelectableSelected = selectableUsers.length > 0 && selectableUsers.every(u => selectedIds.has(u.user_id));
  const someSelected = selectableUsers.some(u => selectedIds.has(u.user_id));

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableUsers.map(u => u.user_id)));
    }
  };

  const toggleSelect = (userId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!count) return;
    if (!(await window.appConfirm(`Permanently delete ${count} selected user${count > 1 ? 's' : ''}? This cannot be undone.`))) return;
    setDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/bulk-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_ids: Array.from(selectedIds) }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Request failed (${res.status})`); }
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(data.message);
      setSelectedIds(new Set());
      loadUsers();
    } catch (e) { toast.error(e.message); }
    setDeleting(false);
  };

  const pendingUsers = users.filter(u => u.admin_approved === false || (u.email_verified === false && u.admin_approved === undefined));

  const openViewUser = (u) => {
    const p = u.profile || {};
    setEditForm({
      name: u.name || '', email: u.email || '',
      role: p.role || '', organisation: p.organisation || '',
      sector: p.sector || '', location: p.location || '',
      country: p.country || '', city: p.city || '',
      region: p.region || '', background: p.background || '',
      gender: p.gender || '', age_group: p.age_group || '',
      bio: p.bio || '', linkedin_url: p.linkedin_url || '',
      mobile_number: p.mobile_number || '',
      working_on: p.working_on || '',
      years_experience: p.years_experience || '',
      self_description: p.self_description || '',
      expertise: (p.expertise || []).join(', '),
      interests: (p.interests || []).join(', '),
      challenges: (p.challenges || []).join(', '),
      looking_for: (p.looking_for || []).join(', '),
    });
    setViewUser(u);
    setEditMode(false);
  };

  const handleSaveProfile = async () => {
    if (!viewUser) return;
    setSaving(true);
    try {
      const toArr = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];
      const body = {
        name: editForm.name,
        email: editForm.email,
        profile: {
          role: editForm.role, organisation: editForm.organisation,
          sector: editForm.sector, location: editForm.location,
          country: editForm.country, city: editForm.city,
          region: editForm.region, background: editForm.background,
          gender: editForm.gender, age_group: editForm.age_group,
          bio: editForm.bio, linkedin_url: editForm.linkedin_url,
          mobile_number: editForm.mobile_number, working_on: editForm.working_on,
          years_experience: editForm.years_experience ? parseInt(editForm.years_experience) : null,
          self_description: editForm.self_description,
          expertise: toArr(editForm.expertise),
          interests: toArr(editForm.interests),
          challenges: toArr(editForm.challenges),
          looking_for: toArr(editForm.looking_for),
        }
      };
      const res = await fetch(`${BACKEND_URL}/api/admin/users/${viewUser.user_id}/profile`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Failed'); }
      toast.success('Profile updated');
      setViewUser(null);
      loadUsers();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2">User Management</h3>
        <p className="text-sm text-slate-600">Search, filter, manage roles, suspend or delete users.</p>
      </div>

      {/* Pending Approvals Section */}
      {pendingUsers.length > 0 && (
        <Card className="p-4 border-2 border-amber-300 bg-amber-50/50 mb-4" data-testid="pending-approvals-section">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="h-5 w-5 text-amber-600" />
            <h4 className="font-bold text-amber-800">Pending Approvals ({pendingUsers.length})</h4>
          </div>
          <div className="space-y-2">
            {pendingUsers.map(u => (
              <div key={u.user_id} data-testid={`pending-user-${u.user_id}`}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center gap-3 min-w-0">
                  <MemberAvatar name={u.name} picture={u.picture} size={36} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{u.name}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    <p className="text-xs text-slate-400">Registered {new Date(u.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button data-testid={`approve-user-${u.user_id}`} onClick={() => handleApproveUser(u.user_id, u.name)}
                    size="sm" className="bg-brand-sage text-white hover:bg-brand-sage/90 gap-1.5">
                    <UserCheck className="h-3.5 w-3.5" /> Approve
                  </Button>
                  <Button data-testid={`reject-user-${u.user_id}`} onClick={() => handleDeleteUser(u.user_id, u.name)}
                    size="sm" variant="outline" className="text-red-500 hover:bg-red-50 gap-1.5">
                    <Trash2 className="h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="user-search-input" placeholder="Search by name, email or organisation..."
            value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
          <SelectTrigger data-testid="user-role-filter" className="w-44"><SelectValue placeholder="Filter by role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="member">Member</SelectItem>
            <SelectItem value="verified_attendee">Verified Attendee</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
          <SelectTrigger data-testid="user-status-filter" className="w-44"><SelectValue placeholder="Filter by status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Select All bar + Delete Selected */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-navy transition-colors"
            data-testid="select-all-users" title={allSelectableSelected ? 'Deselect all' : 'Select all'}>
            {allSelectableSelected ? (
              <CheckSquare className="h-4.5 w-4.5 text-brand-navy" />
            ) : someSelected ? (
              <MinusSquare className="h-4.5 w-4.5 text-brand-navy" />
            ) : (
              <Square className="h-4.5 w-4.5" />
            )}
            <span>{allSelectableSelected ? 'Deselect all' : 'Select all'}</span>
          </button>
          <span className="text-xs text-slate-500">{filteredUsers.length} of {users.length} users shown</span>
        </div>
        {selectedIds.size > 0 && (
          <Button data-testid="bulk-delete-btn" onClick={handleBulkDelete} disabled={deleting}
            variant="destructive" size="sm" className="gap-1.5">
            <Trash2 className="h-3.5 w-3.5" />
            {deleting ? 'Deleting...' : `Delete Selected (${selectedIds.size})`}
          </Button>
        )}
      </div>

      <div className="grid gap-3">
        {filteredUsers.map(u => {
          const canSelect = u.user_id !== currentUser?.user_id && u.role !== 'admin';
          const isSelected = selectedIds.has(u.user_id);
          return (
            <Card key={u.user_id} data-testid={`admin-user-${u.user_id}`}
              className={`p-5 hover:shadow-lg transition-shadow ${u.status === 'suspended' ? 'bg-red-50 border-red-200' : ''} ${isSelected ? 'ring-2 ring-brand-navy/30 bg-brand-navy/[0.02]' : ''}`}>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center space-x-4 flex-1 min-w-0">
                  {/* Checkbox */}
                  {canSelect ? (
                    <button onClick={() => toggleSelect(u.user_id)} className="flex-shrink-0"
                      data-testid={`select-user-${u.user_id}`} title={isSelected ? 'Deselect' : 'Select'}>
                      {isSelected ? (
                        <CheckSquare className="h-5 w-5 text-brand-navy" />
                      ) : (
                        <Square className="h-5 w-5 text-slate-300 hover:text-slate-500" />
                      )}
                    </button>
                  ) : (
                    <div className="w-5 flex-shrink-0" />
                  )}

                  <label
                      htmlFor={`admin-upload-photo-${u.user_id}`}
                      title={u.has_picture ? 'Replace profile photo for this user' : 'Upload profile photo for this user'}
                      data-testid={`admin-upload-photo-trigger-${u.user_id}`}
                      className="relative group cursor-pointer flex-shrink-0"
                    >
                      <MemberAvatar
                        key={photoBust[u.user_id] || 0}
                        name={u.name}
                        picture={u.has_picture ? (photoBust[u.user_id] ? `${u.picture}?v=${photoBust[u.user_id]}` : u.picture) : undefined}
                        size={44}
                        className={u.status === 'suspended' ? 'opacity-50 grayscale' : ''}
                      />
                      <span className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="w-4 h-4 text-white" />
                      </span>
                      <input
                        id={`admin-upload-photo-${u.user_id}`}
                        data-testid={`admin-upload-photo-input-${u.user_id}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          handleAdminUploadPhoto(u.user_id, u.name, f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold font-heading truncate">{u.name}</h3>
                      {u.status === 'suspended' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">Suspended</span>
                      )}
                      {u.force_password_change && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">PW Change Required</span>
                      )}
                      {u.user_id === currentUser?.user_id && (
                        <span className="px-2 py-0.5 bg-brand-blue/20 text-brand-navy text-xs font-medium rounded-full">You</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {u.profile?.organisation && `${u.profile.organisation} • `}
                      {u.profile?.role && `${u.profile.role} • `}
                      Joined {new Date(u.created_at).toLocaleDateString()}
                    </p>
                    {/* Consents — surfaces what the user agreed to in the
                        post-approval modal. Required boxes are flagged red
                        when missing so admins can see at a glance who's
                        bypassed consent (e.g. older accounts). */}
                    {(() => {
                      const c = u.consents || {};
                      const hasAnyConsentTracking = c.consented_at || c.terms_accepted || c.privacy_accepted;
                      if (!hasAnyConsentTracking) {
                        return (
                          <p className="text-[10px] text-slate-300 mt-0.5 italic" data-testid={`user-consents-${u.user_id}`}>
                            No consent recorded yet
                          </p>
                        );
                      }
                      const pill = (ok, label, required) => (
                        <span
                          title={label + (ok ? ' — agreed' : ' — not agreed')}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            ok
                              ? 'bg-green-100 text-green-700'
                              : required
                                ? 'bg-red-100 text-red-700'
                                : 'bg-slate-100 text-slate-400'
                          }`}
                        >
                          {ok ? '✓' : '·'} {label}
                        </span>
                      );
                      return (
                        <div className="flex flex-wrap items-center gap-1 mt-1" data-testid={`user-consents-${u.user_id}`}>
                          {pill(c.terms_accepted, 'Terms', true)}
                          {pill(c.privacy_accepted, 'Privacy', true)}
                          {pill(c.marketing_opt_in, 'Marketing', false)}
                          {pill(c.partner_comms_opt_in, 'Partner comms', false)}
                          {c.consented_at && (
                            <span className="text-[10px] text-slate-400 ml-1">
                              {new Date(c.consented_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="flex items-center gap-1">
                    <Button data-testid={`view-user-${u.user_id}`} onClick={() => openViewUser(u)}
                      variant="outline" size="sm" className="text-brand-navy hover:bg-brand-navy/5" title="View profile">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button data-testid={`edit-user-${u.user_id}`} onClick={() => { openViewUser(u); setEditMode(true); }}
                      variant="outline" size="sm" className="text-teal-600 hover:bg-teal-50" title="Edit profile">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 flex justify-center">
                      {u.role === 'admin' && <Shield className="h-5 w-5 text-red-600" />}
                      {u.role === 'verified_attendee' && <UserCheck className="h-5 w-5 text-brand-sage" />}
                      {u.role === 'member' && <User className="h-5 w-5 text-slate-400" />}
                    </div>
                    <Select value={u.role} onValueChange={(newRole) => handleRoleChange(u.user_id, newRole)}
                      disabled={u.user_id === currentUser?.user_id}>
                      <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-44 h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="z-50">
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="verified_attendee">Verified Attendee</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {canSelect && (
                    <div className="flex items-center gap-1">
                      {u.auth_provider !== 'google' && (
                        <Button data-testid={`force-pw-${u.user_id}`} onClick={() => handleForcePasswordChange(u.user_id, u.name)}
                          variant="outline" size="sm"
                          className="text-amber-600 hover:bg-amber-50" title="Force password change">
                          <KeyRound className="h-4 w-4" />
                        </Button>
                      )}
                      <Button data-testid={`suspend-user-${u.user_id}`} onClick={() => handleSuspendUser(u.user_id)}
                        variant="outline" size="sm"
                        className={u.status === 'suspended' ? 'text-brand-sage hover:bg-brand-sage/10' : 'text-amber-600 hover:bg-amber-50'}
                        title={u.status === 'suspended' ? 'Reactivate user' : 'Suspend user'}>
                        {u.status === 'suspended' ? <RotateCcw className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                      </Button>
                      <Button data-testid={`delete-user-${u.user_id}`} onClick={() => handleDeleteUser(u.user_id, u.name)}
                        variant="outline" size="sm" className="text-red-600 hover:bg-red-50" title="Delete user permanently">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* View/Edit User Dialog */}
      <Dialog open={!!viewUser} onOpenChange={(v) => { if (!v) { setViewUser(null); setEditMode(false); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <User className="h-5 w-5 text-brand-navy" />
                {editMode ? 'Edit User Profile' : 'User Profile'}
              </span>
              {!editMode && (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="switch-to-edit-btn" className="text-teal-600 gap-1.5">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>

          {viewUser && !editMode && (
            <div className="space-y-4" data-testid="user-view-mode">
              <div className="flex items-center gap-4 pb-4 border-b">
                <MemberAvatar name={viewUser.name} picture={viewUser.picture} size={64} />
                <div>
                  <h3 className="text-lg font-bold">{viewUser.name}</h3>
                  <p className="text-sm text-slate-500">{viewUser.email}</p>
                  <p className="text-xs text-slate-400">Joined {new Date(viewUser.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ['Job Title', viewUser.profile?.role],
                  ['Organisation', viewUser.profile?.organisation],
                  ['Sector', viewUser.profile?.sector],
                  ['Location', viewUser.profile?.location],
                  ['Country', viewUser.profile?.country],
                  ['City', viewUser.profile?.city],
                  ['Region', viewUser.profile?.region],
                  ['Stakeholder Type', viewUser.profile?.background],
                  ['Gender', viewUser.profile?.gender],
                  ['Age Group', viewUser.profile?.age_group],
                  ['Years Experience', viewUser.profile?.years_experience],
                  ['Self Description', viewUser.profile?.self_description],
                  ['LinkedIn', viewUser.profile?.linkedin_url],
                  ['Mobile', viewUser.profile?.mobile_number],
                ].map(([label, val]) => (
                  <div key={label}>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
                    <p className="text-slate-700 mt-0.5">{val || <span className="text-slate-300 italic">Not set</span>}</p>
                  </div>
                ))}
              </div>
              {viewUser.profile?.bio && (
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">About</p>
                  <RichText html={viewUser.profile.bio} className="text-sm text-slate-700 leading-relaxed" />
                </div>
              )}
              {(() => {
                const tags = getTraitTags(viewUser.profile || {});
                if (tags.length === 0) return null;
                return (
                  <div data-testid="user-behavioural-tags">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Behavioural Profile</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(t => (
                        <span key={t} className={`px-2 py-0.5 text-xs font-medium rounded-full border ${TAG_COLORS[t] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{t}</span>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {viewUser.profile?.working_on && (
                <div>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Working On</p>
                  <p className="text-sm text-slate-700">{viewUser.profile.working_on}</p>
                </div>
              )}
              {[
                ['Expertise', viewUser.profile?.expertise],
                ['Interests', viewUser.profile?.interests],
                ['Challenges', viewUser.profile?.challenges],
                ['Looking For', viewUser.profile?.looking_for],
              ].map(([label, arr]) => arr?.length > 0 && (
                <div key={label}>
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{label}</p>
                  <div className="flex flex-wrap gap-1">
                    {arr.map(v => <span key={v} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{v}</span>)}
                  </div>
                </div>
              ))}

              {/* Events Attended → controls Keynotes access */}
              <UserAttendedEventsSection userId={viewUser.user_id} />
            </div>
          )}

          {viewUser && editMode && (
            <div className="space-y-4" data-testid="user-edit-mode">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Full Name</Label><Input data-testid="edit-name" value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="mt-1" /></div>
                <div><Label>Email</Label><Input data-testid="edit-email" value={editForm.email} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Job Title</Label><Input data-testid="edit-role" value={editForm.role} onChange={e => setEditForm(p => ({...p, role: e.target.value}))} className="mt-1" /></div>
                <div><Label>Organisation</Label><Input data-testid="edit-organisation" value={editForm.organisation} onChange={e => setEditForm(p => ({...p, organisation: e.target.value}))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Sector</Label><Input data-testid="edit-sector" value={editForm.sector} onChange={e => setEditForm(p => ({...p, sector: e.target.value}))} className="mt-1" /></div>
                <div><Label>Stakeholder Type</Label><Input data-testid="edit-background" value={editForm.background} onChange={e => setEditForm(p => ({...p, background: e.target.value}))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Location</Label><Input data-testid="edit-location" value={editForm.location} onChange={e => setEditForm(p => ({...p, location: e.target.value}))} className="mt-1" /></div>
                <div><Label>Country</Label><Input data-testid="edit-country" value={editForm.country} onChange={e => setEditForm(p => ({...p, country: e.target.value}))} className="mt-1" /></div>
                <div><Label>City</Label><Input data-testid="edit-city" value={editForm.city} onChange={e => setEditForm(p => ({...p, city: e.target.value}))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Region</Label><Input value={editForm.region} onChange={e => setEditForm(p => ({...p, region: e.target.value}))} className="mt-1" /></div>
                <div><Label>Gender</Label><Input value={editForm.gender} onChange={e => setEditForm(p => ({...p, gender: e.target.value}))} className="mt-1" /></div>
                <div><Label>Age Group</Label><Input value={editForm.age_group} onChange={e => setEditForm(p => ({...p, age_group: e.target.value}))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>LinkedIn URL</Label><Input value={editForm.linkedin_url} onChange={e => setEditForm(p => ({...p, linkedin_url: e.target.value}))} className="mt-1" /></div>
                <div><Label>Mobile</Label><Input value={editForm.mobile_number} onChange={e => setEditForm(p => ({...p, mobile_number: e.target.value}))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Years Experience</Label><Input type="number" value={editForm.years_experience} onChange={e => setEditForm(p => ({...p, years_experience: e.target.value}))} className="mt-1" /></div>
                <div><Label>Self Description</Label><Input value={editForm.self_description} onChange={e => setEditForm(p => ({...p, self_description: e.target.value}))} className="mt-1" /></div>
              </div>
              <div><Label>About / Bio</Label><RichTextEditor value={editForm.bio} onChange={v => setEditForm(p => ({...p, bio: v}))} testId="edit-bio" minHeight="6rem" /></div>
              <div><Label>Working On</Label><Input value={editForm.working_on} onChange={e => setEditForm(p => ({...p, working_on: e.target.value}))} className="mt-1" /></div>
              <div><Label>Expertise <span className="text-xs text-slate-400 font-normal">(comma-separated)</span></Label><Input data-testid="edit-expertise" value={editForm.expertise} onChange={e => setEditForm(p => ({...p, expertise: e.target.value}))} className="mt-1" /></div>
              <div><Label>Interests <span className="text-xs text-slate-400 font-normal">(comma-separated)</span></Label><Input value={editForm.interests} onChange={e => setEditForm(p => ({...p, interests: e.target.value}))} className="mt-1" /></div>
              <div><Label>Challenges <span className="text-xs text-slate-400 font-normal">(comma-separated)</span></Label><Input value={editForm.challenges} onChange={e => setEditForm(p => ({...p, challenges: e.target.value}))} className="mt-1" /></div>
              <div><Label>Looking For <span className="text-xs text-slate-400 font-normal">(comma-separated)</span></Label><Input value={editForm.looking_for} onChange={e => setEditForm(p => ({...p, looking_for: e.target.value}))} className="mt-1" /></div>
              <div className="flex gap-2 pt-2">
                <Button data-testid="save-user-profile-btn" onClick={handleSaveProfile} disabled={saving} className="flex-1 bg-brand-navy hover:bg-brand-dark text-white gap-1.5">
                  <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)} data-testid="cancel-edit-btn">Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * Inline section embedded in the user-view dialog. Lists events the user has
 * historically attended (drives Keynote access) with ✕ remove buttons and a
 * dropdown to add historic events.
 */
function UserAttendedEventsSection({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [selectedToAdd, setSelectedToAdd] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`/api/admin/users/${userId}/attended-events`, { credentials: 'include' });
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { if (userId) load(); }, [userId]);

  const addEvent = async () => {
    if (!selectedToAdd) return;
    setAdding(true);
    try {
      const r = await fetch(`/api/admin/users/${userId}/attended-events/${selectedToAdd}`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed');
      toast.success('Event added — keynote access granted');
      setSelectedToAdd('');
      load();
    } catch (e) { toast.error('Could not add event'); }
    setAdding(false);
  };

  const removeEvent = async (eventId, title) => {
    if (!window.confirm(`Remove "${title}" from this user's attended list? They'll lose keynote access if this was their only attendance.`)) return;
    setRemoving(eventId);
    try {
      const r = await fetch(`/api/admin/users/${userId}/attended-events/${eventId}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed');
      toast.success('Event removed');
      load();
    } catch (e) { toast.error('Could not remove event'); }
    setRemoving(null);
  };

  if (loading) return null;
  if (!data) return null;

  return (
    <div className="border-t border-slate-100 pt-4 mt-2" data-testid="user-attended-events">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
          Events Attended {data.has_keynote_access && (
            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-bold normal-case">
              KEYNOTE ACCESS
            </span>
          )}
        </p>
      </div>
      {data.attended_events.length === 0 ? (
        <p className="text-xs text-slate-400 italic mb-3">No event attendance recorded yet — they currently cannot access Keynotes.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {data.attended_events.map(ev => (
            <span
              key={ev.event_id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs text-emerald-800"
              data-testid={`attended-pill-${ev.event_id}`}
            >
              <span>{ev.title}</span>
              <button
                onClick={() => removeEvent(ev.event_id, ev.title)}
                disabled={removing === ev.event_id}
                className="text-emerald-500 hover:text-rose-600 disabled:opacity-30"
                title="Remove (revoke keynote access if this was their only attendance)"
                data-testid={`remove-attended-${ev.event_id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {data.available_events.length > 0 && (
        <div className="flex items-center gap-2">
          <Select value={selectedToAdd} onValueChange={setSelectedToAdd}>
            <SelectTrigger className="flex-1 h-8 text-xs" data-testid="attended-add-select">
              <SelectValue placeholder="Add historic event…" />
            </SelectTrigger>
            <SelectContent>
              {data.available_events.map(ev => (
                <SelectItem key={ev.event_id} value={ev.event_id}>
                  {ev.title}{ev.date ? ` (${ev.date})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={addEvent}
            disabled={!selectedToAdd || adding}
            data-testid="attended-add-btn"
            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </div>
      )}
    </div>
  );
}

